import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Globe, Loader, ArrowUpCircle, Upload, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import type { Profile } from '../hooks/useProfile';
import SocialLinksEditor from '../components/SocialLinksEditor';
import PenNameManager from '../components/PenNameManager';
import type { SocialLinks } from '../components/SocialLinks';
import BackgroundColorPicker from '../components/BackgroundColorPicker';

export default function Settings() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [upgrading, setUpgrading] = useState(false);
  
  // Form state
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [bio, setBio] = useState('');
  const [website, setWebsite] = useState('');
  const [socialLinks, setSocialLinks] = useState<SocialLinks>({});
  const [avatar, setAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<File | null>(null);
  const [backgroundPreview, setBackgroundPreview] = useState<string | null>(null);
  const [emailNotifications, setEmailNotifications] = useState(true);

  // Get current identity
  const currentIdentityId = localStorage.getItem('current_identity_id');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
    
    if (user) {
      fetchProfile();
    }
  }, [user, authLoading, currentIdentityId]);

  const fetchProfile = async () => {
    try {
      // If we have a current identity ID and it's not the main profile,
      // fetch the pen name profile instead
      if (currentIdentityId && currentIdentityId !== user!.id) {
        const { data, error } = await supabase
          .from('pen_names')
          .select('*')
          .eq('id', currentIdentityId)
          .single();

        if (error) throw error;
        setProfile(data);
        setUsername(data.username);
        setFullName(data.full_name || '');
        setBio(data.bio || '');
        setWebsite(data.website || '');
        setSocialLinks(data.social_links || {});
        if (data.avatar_url) {
          setAvatarPreview(data.avatar_url);
        }
      } else {
        // Fetch main profile
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user!.id)
          .single();

        if (error) throw error;
        setProfile(data);
        setUsername(data.username);
        setFullName(data.full_name || '');
        setBio(data.bio || '');
        setWebsite(data.website || '');
        setSocialLinks(data.social_links || {});
        setEmailNotifications(data.email_notifications);
        if (data.avatar_url) {
          setAvatarPreview(data.avatar_url);
        }
        if (data.background_image_url) {
          setBackgroundPreview(data.background_image_url);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setSaving(true);
      setError(null);

      let avatarUrl = profile?.avatar_url;
      if (avatar) {
        const fileExt = avatar.name.split('.').pop();
        const fileName = `${currentIdentityId || user.id}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(fileName, avatar, { upsert: true });

        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(fileName);
          
        avatarUrl = publicUrl;
      }

      let backgroundUrl = profile?.background_image_url;
      if (backgroundImage) {
        const fileExt = backgroundImage.name.split('.').pop();
        const fileName = `${user.id}/background.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('backgrounds')
          .upload(fileName, backgroundImage, { upsert: true });

        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('backgrounds')
          .getPublicUrl(fileName);
          
        backgroundUrl = publicUrl;
      }

      // Update either pen name or main profile
      if (currentIdentityId && currentIdentityId !== user.id) {
        const { error: updateError } = await supabase
          .from('pen_names')
          .update({
            username,
            full_name: fullName,
            bio,
            website,
            avatar_url: avatarUrl,
            social_links: socialLinks
          })
          .eq('id', currentIdentityId);

        if (updateError) throw updateError;
      } else {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            username,
            full_name: fullName,
            bio,
            website,
            avatar_url: avatarUrl,
            background_image_url: backgroundUrl,
            social_links: socialLinks,
            email_notifications: emailNotifications,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);

        if (updateError) throw updateError;
      }

      setSuccess('Profile updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader className="w-8 h-8 animate-spin text-accent1" />
      </div>
    );
  }

  // Check if we should show the pen names section
  const showPenNames = location.hash === '#pen-names';

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {success}
        </div>
      )}

      {/* Role Upgrade Section - Only show for main profile */}
      {!currentIdentityId && profile?.role === 'reader' && (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-md p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-bold mb-2">Upgrade to Author</h3>
              <p className="text-indigo-100 mb-4">
                Ready to share your stories? Upgrade your account to access author features like creating book ideas, managing characters, and engaging with readers.
              </p>
            </div>
            <ArrowUpCircle className="w-8 h-8 text-indigo-200" />
          </div>
          <button
            onClick={async () => {
              try {
                setUpgrading(true);
                setError(null);

                const { error: updateError } = await supabase
                  .from('profiles')
                  .update({
                    role: 'author',
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', user!.id);

                if (updateError) throw updateError;

                await fetchProfile();
                setSuccess('Your account has been upgraded to Author status!');
                setTimeout(() => setSuccess(null), 5000);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to upgrade account');
              } finally {
                setUpgrading(false);
              }
            }}
            disabled={upgrading}
            className="px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 disabled:opacity-50"
          >
            {upgrading ? 'Upgrading...' : 'Upgrade Now'}
          </button>
        </div>
      )}

      {showPenNames ? (
        <PenNameManager />
      ) : (
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {currentIdentityId && currentIdentityId !== user?.id ? 'Pen Name Settings' : 'Profile Settings'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile Picture
              </label>
              <div className="flex items-center gap-4">
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Avatar preview"
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 bg-accent3/20 rounded-full flex items-center justify-center">
                    <User className="w-10 h-10 text-accent1" />
                  </div>
                )}
                <label className="flex-1">
                  <div className="px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-center">
                    Choose new picture
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setAvatar(file);
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setAvatarPreview(reader.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </label>
              </div>
            </div>

            {/* Only show background image option for main profile */}
            {!currentIdentityId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Background
                </label>
                <div className="space-y-4">
                  {backgroundPreview && (
                    <div className="relative">
                      <img
                        src={backgroundPreview}
                        alt="Background preview"
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          if (!user || !profile?.background_image_url) return;
                          try {
                            setSaving(true);
                            setError(null);

                            const fileName = profile.background_image_url.split('/').pop();
                            if (!fileName) throw new Error('Invalid background image URL');

                            const { error: storageError } = await supabase.storage
                              .from('backgrounds')
                              .remove([fileName]);

                            if (storageError) throw storageError;

                            const { error: updateError } = await supabase
                              .from('profiles')
                              .update({
                                background_image_url: null,
                                updated_at: new Date().toISOString()
                              })
                              .eq('id', user.id);

                            if (updateError) throw updateError;

                            setBackgroundPreview(null);
                            setBackgroundImage(null);
                            setSuccess('Background image removed successfully');
                            setTimeout(() => setSuccess(null), 3000);
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Failed to remove background image');
                          } finally {
                            setSaving(false);
                          }
                        }}
                        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <label className="block">
                    <div className="px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-center">
                      <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                      {backgroundPreview ? 'Choose new background' : 'Choose background'}
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setBackgroundImage(file);
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setBackgroundPreview(reader.result as string);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </label>
                  <p className="text-sm text-gray-500">
                    Recommended size: 1500x500 pixels. Max file size: 5MB.
                  </p>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent1 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent1 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bio
              </label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent1 focus:border-transparent"
                rows={4}
              />
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Links
              </label>
              
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-gray-400" />
                  <input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent1 focus:border-transparent"
                    placeholder="Website URL"
                  />
                </div>
                
                <SocialLinksEditor
                  links={socialLinks}
                  onChange={setSocialLinks}
                />
              </div>
            </div>

            {/* Only show email notifications for main profile */}
            {!currentIdentityId && (
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    className="h-4 w-4 text-accent1 focus:ring-accent1 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">
                    Receive email notifications for activity updates
                  </span>
                </label>
                <p className="text-sm text-gray-500 ml-6">
                  You'll get a daily digest of new comments, polls, milestones, and other important updates.
                </p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <BackgroundColorPicker />
              <button
                type="submit"
                disabled={saving || !username}
                className="bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}